import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Container, Grid, IconButton, Tooltip } from "@material-ui/core";
import { DataGrid } from "@material-ui/data-grid";
import AddOutlinedIcon from "@material-ui/icons/AddOutlined";
import CheckOutlinedIcon from "@material-ui/icons/CheckOutlined";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";

import { useHistory } from "react-router-dom";

import SearchInput from "../../components/basic/SearchInput";
import { TitleTable } from "../../components/basic/TitleTable";
import { getCustomers } from "../../repositories/v1/customer";
import { GridStyled, IconButtonStyled } from "../../styles/GlobalStyle";
import { formatDateBrazilian } from "../../utils/formatDate";
import MaskService from "../../utils/mask/maskService";

export default function PreviewCustomer() {
  const [page, setPage] = useState(0);
  const [rowsAtListing, setRowsAtListing] = useState([]);
  const [numberOfClients, setNumberOfClients] = useState(0);
  const [loading, setLoading] = useState(false);
  const [textFilter, setTextFilter] = useState("");
  const searchRef = useRef(null);
  const { push } = useHistory();

  const columns = [
    {
      field: "Acoes",
      headerName: "Acões",
      width: 80,
      renderCell: ({ row }) => (
        <Tooltip title="Editar Cliente">
          <IconButton onClick={() => push("/updateCustomer", row)}>
            <EditOutlinedIcon />
          </IconButton>
        </Tooltip>
      ),
      align: "center",
    },
    {
      field: "CNPJ",
      headerName: "CNPJ/CPF",
      width: 161,
      renderCell: ({ row }) =>
        MaskService.toMask(
          row?.CNPJ.length > 11 ? "cnpj" : "cpf",
          row?.CNPJ || ""
        ),
    },
    {
      field: "Razao",
      headerName: "Razão",
      minWidth: 250,
      flex: 1,
      renderCell: ({ row }) => row?.Razao,
    },
    {
      field: "Email",
      headerName: "E-mail",
      minWidth: 250,
      flex: 1,
      renderCell: ({ row }) => row?.Email,
    },
    {
      field: "FlagContador",
      headerName: "Escritório",
      width: 92,
      renderCell: ({ row }) =>
        row?.FlagContador ? <CheckOutlinedIcon /> : "-",
      align: "center",
    },
    {
      field: "Data",
      headerName: "Data",
      width: 155,
      renderCell: ({ row }) => formatDateBrazilian(row?.createdAt, true),
      align: "center",
    },
  ];

  useEffect(() => {
    (async () => {
      setRowsAtListing([]);
      try {
        setLoading(true);

        const query = ["CNPJ", "Razao", "Fantasia", "Email"].map(
          (element) => `${element}=${textFilter}`
        );
        const { data: respReport } = await getCustomers(
          `?pageNumber=${page}${textFilter ? `&${query.join("&")}` : ""}`
        );

        setRowsAtListing(respReport.data.rows);
        setNumberOfClients(respReport.data.count);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    })();
  }, [page, textFilter]);

  const debounce = useMemo(
    () => (func) => {
      let timer;
      return function (...args) {
        const context = this;
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
          timer = null;
          func.apply(context, args);
        }, 500);
      };
    },
    []
  );

  const searchFilters = useCallback(async (searchParam) => {
    setPage(0);
    setRowsAtListing([]);
    setTextFilter(searchParam);
  }, []);

  const handleChangeFilter = debounce(searchFilters);

  return (
    <Container maxWidth="lg">
      <GridStyled
        container
        padding="20px 0 20px 0"
        justify="space-between"
        spacing={3}
      >
        <Grid item xs={12}>
          <TitleTable
            inputRef={searchRef}
            labelFilter="Filtro (CNPJ, Razão, Fantasia, E-mail)"
            title="Clientes"
            loading={loading}
            children={
              <Grid item container sm>
                <Grid item sm></Grid>
                <Grid item sm={6}>
                  <SearchInput
                    placeholder="Filtro (CNPJ, Razão, Fantasia, E-mail)"
                    handleChange={(event) =>
                      handleChangeFilter(event.target.value)
                    }
                  />
                </Grid>
                <Grid item sm></Grid>
                <Grid item>
                  <Tooltip title="Adicionar Cliente">
                    <IconButtonStyled onClick={() => push("/updateCustomer")}>
                      <AddOutlinedIcon />
                    </IconButtonStyled>
                  </Tooltip>
                </Grid>
              </Grid>
            }
          />
        </Grid>
        <Grid item xs={12}>
          <DataGrid
            autoHeight
            disableColumnMenu
            loading={loading}
            rows={rowsAtListing}
            rowCount={numberOfClients}
            columns={columns}
            pageSize={10}
            getRowId={(row) => row.CNPJ}
            page={page}
            onPageChange={(e) => setPage(e?.page || 0)}
            pagination
            paginationMode="server"
          />
        </Grid>
      </GridStyled>
    </Container>
  );
}
